import { cn } from '@/shared/utils/cn';
import type {
  CatalogCardCatalogPropsFlat,
  CatalogCardInteractionContext,
  CatalogCardMediaIndicator,
  CatalogCardPrimaryVariant,
} from '../../types';
import { catalogCardDefaultMediaIndicators } from '../../constants/mediaIndicators';
import {
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import type { PopupRef } from '@/shared/types/popup';
import { Picture } from '@/modules/Picture';
import { Badge } from '@/modules/Badge';
import { Icon } from '@/modules/Icon';
import { Button } from '@/modules/Button';
import { Popup } from '@/modules/Popup';
import { Tooltip, TruncatedWithTooltip } from '@/modules/Tooltip';
import {
  faBookmarkPlus,
  faCircleCheck,
  faEllipsis,
  faEye,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faBadgeCheck } from '@fortawesome/pro-duotone-svg-icons';
import { faLocationDot, faStar } from '@fortawesome/pro-solid-svg-icons';
import { OptionsMenuPanel, PrimaryMenuPanel } from './catalogCardMenuPanels';
import { stopCardClick } from './catalogCardMenuPanelUtils';
import { CatalogCatalogCardTagsRow } from './catalogCardTagsRow';
import { normalizeCatalogCardTags } from '../../utils/catalogCardTagUtils';
import { getCatalogCardRatingDisplayText } from '../../utils/catalogCardRatingDisplay';
import { isCatalogCardMediaIndicatorText } from '../../utils/catalogCardMediaIndicatorUtils';

const defaultPrimaryLabels = {
  add: 'Agregar',
  added: 'Agregado',
  remove: 'Quitar',
  deleted: 'Eliminado',
} as const;

const defaultCatalogOptionsTooltip = 'Más opciones';
const defaultCatalogRatingTooltip = 'Calificación del medio';

/**
 * CTA *Agregado* con hover (check → xmark, texto → *Quitar*).
 * Fila `inline-flex` + `mx-auto` para centrar el grupo en el botón; la caja de texto
 * mide `max(Agregado n/m, Quitar)` con grid invisible; las capas usan `grid place-items-center`
 * para que “Quitar” no quede alineado a la izquierda dentro de la caja ancha.
 */
function CatalogAddedHoverTriggerContent({
  labelText,
  showLeadingIcon = true,
}: {
  labelText: string;
  showLeadingIcon?: boolean;
}) {
  const textLayerInner =
    'ui-block ui-w-max ui-max-w-full ui-whitespace-nowrap ui-text-center ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-none';

  return (
    <span className="ui-mx-auto ui-inline-flex ui-max-w-full ui-items-center ui-justify-center ui-gap-8">
      {showLeadingIcon ? (
        <span className="ui-relative ui-flex ui-h-16 ui-w-16 ui-shrink-0 ui-items-center ui-justify-center">
          <Icon
            name={faCircleCheck}
            size="sm"
            color="current"
            className="ui-transition-opacity group-hover:ui-opacity-0"
            aria-hidden
          />
          <span
            className="ui-pointer-events-none ui-absolute ui-inset-0 ui-grid ui-place-items-center ui-opacity-0 ui-transition-opacity group-hover:ui-opacity-100"
            aria-hidden
          >
            <Icon name={faXmark} size="sm" color="current" />
          </span>
        </span>
      ) : null}
      <span className="ui-relative ui-inline-grid ui-shrink-0">
        <span className="ui-invisible ui-col-start-1 ui-row-start-1 ui-inline-grid ui-grid-cols-1">
          <span className="ui-col-start-1 ui-row-start-1 ui-whitespace-nowrap ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-none">
            {labelText}
          </span>
          <span className="ui-col-start-1 ui-row-start-1 ui-whitespace-nowrap ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-none">
            {defaultPrimaryLabels.remove}
          </span>
        </span>
        <span className="ui-absolute ui-inset-0 ui-grid ui-place-items-center ui-opacity-100 ui-transition-opacity group-hover:ui-opacity-0">
          <span className={textLayerInner}>{labelText}</span>
        </span>
        <span className="ui-absolute ui-inset-0 ui-grid ui-place-items-center ui-opacity-0 ui-transition-opacity group-hover:ui-opacity-100">
          <span className={textLayerInner}>{defaultPrimaryLabels.remove}</span>
        </span>
      </span>
    </span>
  );
}

/**
 * Variante catálogo (250px): media, detalle y CTA cerrados por props.
 */
export const CatalogCatalogCard = forwardRef<HTMLDivElement, CatalogCardCatalogPropsFlat>(
  function CatalogCatalogCard(
    {
      className,
      selected = false,
      emphasized = false,
      subtleBorder = false,
      disabled = false,
      to,
      href,
      cardLinkTarget = '_self',
      onClick,
      imageSrc,
      imageAlt = '',
      showExclusiveBadge = false,
      indicators,
      showAvailabilityIndicators = false,
      categoryLabel,
      ratingValue,
      title,
      locationLabel,
      tags: tagsProp = [],
      primarySelectedTags: primarySelectedTagsControlled,
      defaultPrimarySelectedTags = [],
      onPrimaryTagsSelectionChange,
      primaryTagsSelectAllLabel,
      primaryTagsShowSelectAll = false,
      optionsPressed = false,
      primaryLinkHref,
      primaryLinkTo,
      primaryLinkLabel = 'Ver más',
      primaryLinkTarget = '_self',
      primaryVariant = 'add',
      primaryLabel,
      primaryIcon: primaryIconProp,
      onPrimaryClick,
      onOptionsClick,
      optionsMenuItems,
      primaryMenuItems,
      primaryMenuFooter,
      primaryMenuMaxHeightClass,
      optionsTooltip,
      ratingTooltip,
    },
    ref,
  ) {
    const optionsMenuPopupRef = useRef<PopupRef>(null);
    const primaryMenuPopupRef = useRef<PopupRef>(null);
    const [internalPrimarySelectedTags, setInternalPrimarySelectedTags] = useState<string[]>(() => [
      ...defaultPrimarySelectedTags,
    ]);

    const tagsContentSignature = normalizeCatalogCardTags(tagsProp)
      .map((t) => `${t.label}\u0000${t.color}`)
      .join('\u0001');
    const resolvedTags = useMemo(() => normalizeCatalogCardTags(tagsProp), [tagsContentSignature]);
    const tagLabels = resolvedTags.map((t) => t.label);
    const tagsPrimaryCheckboxMode = Boolean(onPrimaryTagsSelectionChange && tagLabels.length > 0);
    const primarySelectedTags = primarySelectedTagsControlled ?? internalPrimarySelectedTags;
    const hasPrimaryTagSelection = primarySelectedTags.length > 0;

    const cardContext: CatalogCardInteractionContext = useMemo(
      () => ({ family: 'catalog', title, primaryVariant }),
      [title, primaryVariant],
    );

    useEffect(() => {
      if (primarySelectedTagsControlled !== undefined) return;
      setInternalPrimarySelectedTags((prev) => prev.filter((t) => tagLabels.includes(t)));
      // Solo tagsContentSignature (no el array crudo): evita bucles cuando el padre pasa `tags` inline en cada render.
    }, [tagsContentSignature, primarySelectedTagsControlled]);

    const setPrimarySelectedTags = (next: string[]) => {
      if (primarySelectedTagsControlled === undefined) {
        setInternalPrimarySelectedTags(next);
      }
      onPrimaryTagsSelectionChange?.(next, cardContext);
    };

    const primaryText =
      primaryLabel ?? defaultPrimaryLabels[primaryVariant as CatalogCardPrimaryVariant];
    /** Panel de tags (233:13284): sin selección → *Agregar* + bookmark + primary; con selección → *Agregado n/m* + check + secondary. */
    const variantPrimaryIcon =
      tagsPrimaryCheckboxMode && tagLabels.length > 0
        ? hasPrimaryTagSelection
          ? primaryVariant === 'add'
            ? faBookmarkPlus
            : primaryVariant === 'added'
              ? faCircleCheck
              : primaryVariant === 'deleted'
                ? faTrashCan
                : faXmark
          : faBookmarkPlus
        : primaryVariant === 'add'
          ? faBookmarkPlus
          : primaryVariant === 'added'
            ? faCircleCheck
            : primaryVariant === 'deleted'
              ? faTrashCan
              : faXmark;
    const resolvedVariantPrimaryIcon = primaryIconProp ?? variantPrimaryIcon;
    /** Figma 233:13284: con opciones en el panel, *Agregado n/m* solo cuando hay al menos una etiqueta elegida; si no, *Agregar*. */
    const primaryTriggerLabel =
      tagsPrimaryCheckboxMode && tagLabels.length > 0
        ? hasPrimaryTagSelection
          ? `${primaryText} ${primarySelectedTags.length}/${tagLabels.length}`
          : (primaryLabel ?? defaultPrimaryLabels.add)
        : primaryText;
    const resolvedPrimaryFooter =
      primaryMenuFooter ??
      (tagsPrimaryCheckboxMode ? { label: 'Cerrar', onClick: () => {} } : undefined);
    const hasOptionsMenu = Boolean(optionsMenuItems?.length);
    const primaryLinkDestination = primaryLinkTo ?? primaryLinkHref;
    const isPrimaryLink = Boolean(primaryLinkDestination);
    const hasPrimaryMenu =
      !isPrimaryLink && (Boolean(primaryMenuItems?.length) || tagsPrimaryCheckboxMode);

    /** Panel de tags + *Agregado n/m*: mismo hover que CTA *added* sin panel (Quitar + xmark). */
    const showAddedTagsPanelHoverRemove =
      tagsPrimaryCheckboxMode &&
      tagLabels.length > 0 &&
      hasPrimaryTagSelection &&
      primaryVariant === 'added';

    const primaryMenuPopupLabel = showAddedTagsPanelHoverRemove ? (
      <CatalogAddedHoverTriggerContent labelText={primaryTriggerLabel} />
    ) : (
      primaryTriggerLabel
    );

    const widthClass = 'ui-w-[250px] ui-min-w-[250px] ui-max-w-[250px]';

    const resolvedIndicators: CatalogCardMediaIndicator[] =
      indicators && indicators.length > 0
        ? indicators
        : showAvailabilityIndicators
          ? catalogCardDefaultMediaIndicators
          : [];

    const handleRootClick = (e: MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;
      e.stopPropagation();
      onClick(cardContext);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!onClick) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(cardContext);
      }
    };

    const cardDestination = (to ?? href ?? '').trim();
    const isCardLink = cardDestination.length > 0;
    /** Con `disabled`, no hay `<a>` ni navegación desde la zona principal. */
    const effectiveCardLink = Boolean(isCardLink && !disabled);
    /** Clic/teclado en la raíz: siempre que haya `onClick` y no haya enlace efectivo (incluye tarjeta bloqueada + `onClick`). */
    const rootCardClickActive = Boolean(onClick && !effectiveCardLink);

    const hasCategory = Boolean(categoryLabel?.trim());
    const trimmedRating = (ratingValue ?? '').trim();
    const ratingDisplayText = getCatalogCardRatingDisplayText(trimmedRating);
    /** Hay calificación explícita (texto no vacío). Sin calificación: estrella `disabled` y texto fijo `0.0` (no configurable). */
    const hasRatedValue = trimmedRating.length > 0;
    const showCategoryMetaRow = hasCategory || hasRatedValue;
    const showLocationRow = Boolean(locationLabel?.trim());

    const resolvedOptionsTooltip =
      optionsTooltip === undefined ? defaultCatalogOptionsTooltip : optionsTooltip;
    const resolvedRatingTooltip =
      ratingTooltip === undefined ? defaultCatalogRatingTooltip : ratingTooltip;
    const optionsTriggerTooltip =
      resolvedOptionsTooltip.trim().length > 0 ? resolvedOptionsTooltip : undefined;
    const ratingBlockTooltip =
      resolvedRatingTooltip.trim().length > 0 ? resolvedRatingTooltip : undefined;

    const optionsTrigger = hasOptionsMenu ? (
      <Popup
        ref={optionsMenuPopupRef}
        position="top-left"
        color="secondary"
        size="xs"
        popoverArrow={false}
        pill="pill"
        rounded
        theme="solid"
        icon={faEllipsis}
        tooltip={
          optionsTriggerTooltip
            ? { label: optionsTriggerTooltip, position: 'top' as const }
            : undefined
        }
        buttonClassName={cn(optionsPressed ? 'ui-bg-fill-secondary-hover' : undefined)}
      >
        <OptionsMenuPanel
          items={optionsMenuItems!}
          onDismiss={() => optionsMenuPopupRef.current?.close()}
        />
      </Popup>
    ) : (
      <Button
        color="secondary"
        size="xs"
        pill="pill"
        theme="solid"
        icon={faEllipsis}
        tooltip={
          optionsTriggerTooltip
            ? { label: optionsTriggerTooltip, position: 'top' as const }
            : undefined
        }
        className={cn(optionsPressed ? 'ui-bg-fill-secondary-hover' : undefined)}
        aria-label="Opciones"
        onClick={(e) => {
          stopCardClick(e);
          onOptionsClick?.(cardContext);
        }}
      />
    );

    const primaryCta = isPrimaryLink ? (
      <Button
        {...(disabled ? {} : { link: primaryLinkDestination, target: primaryLinkTarget })}
        color="primary"
        theme="solid"
        size="sm"
        pill="sm"
        leftSlot={primaryIconProp ?? faEye}
        fullWidth
        textAlign="center"
        className="ui-justify-center"
        disabled={disabled}
      >
        {primaryLinkLabel}
      </Button>
    ) : hasPrimaryMenu ? (
      <Popup
        ref={primaryMenuPopupRef}
        position={tagsPrimaryCheckboxMode ? 'top-right' : 'bottom-left'}
        popoverArrow={false}
        color={
          primaryVariant === 'deleted'
            ? 'destructive'
            : tagsPrimaryCheckboxMode && hasPrimaryTagSelection
              ? 'secondary'
              : 'primary'
        }
        size="sm"
        pill="sm"
        absolute
        theme="solid"
        offset={tagsPrimaryCheckboxMode ? 8 : 6}
        fullWidth
        containerClassName="ui-w-full"
        label={primaryMenuPopupLabel}
        leftSlot={showAddedTagsPanelHoverRemove ? undefined : resolvedVariantPrimaryIcon}
        textAlign="center"
        disabled={disabled}
        buttonClassName={cn(
          'ui-min-w-0 ui-justify-center ui-items-center',
          showAddedTagsPanelHoverRemove && 'ui-group',
        )}
      >
        <PrimaryMenuPanel
          items={primaryMenuItems ?? []}
          tags={tagsPrimaryCheckboxMode ? tagLabels : undefined}
          selectedTags={primarySelectedTags}
          onTagsChange={tagsPrimaryCheckboxMode ? setPrimarySelectedTags : undefined}
          selectAllLabel={primaryTagsSelectAllLabel}
          tagsShowSelectAll={primaryTagsShowSelectAll}
          footer={resolvedPrimaryFooter}
          maxHeightClass={primaryMenuMaxHeightClass}
          onDismiss={() => primaryMenuPopupRef.current?.close()}
        />
      </Popup>
    ) : primaryVariant === 'added' ? (
      <Button
        color="secondary"
        theme="solid"
        size="sm"
        pill="sm"
        leftSlot={primaryIconProp}
        fullWidth
        textAlign="center"
        className="ui-group ui-justify-center"
        disabled={disabled}
        onClick={(e) => {
          stopCardClick(e);
          onPrimaryClick?.(cardContext);
        }}
      >
        <CatalogAddedHoverTriggerContent labelText={primaryText} />
      </Button>
    ) : (
      <Button
        color={
          primaryVariant === 'deleted'
            ? 'destructive'
            : primaryVariant === 'add'
              ? 'primary'
              : 'secondary'
        }
        theme="solid"
        size="sm"
        pill="sm"
        leftSlot={resolvedVariantPrimaryIcon}
        fullWidth
        textAlign="center"
        className="ui-justify-center"
        disabled={disabled}
        onClick={(e) => {
          stopCardClick(e);
          onPrimaryClick?.(cardContext);
        }}
      >
        {primaryText}
      </Button>
    );

    const rootClassName = cn(
      'ui-imj',
      !disabled && 'ui-group',
      'ui-relative ui-flex ui-flex-col ui-gap-16 ui-rounded-surface-card ui-border-1 ui-pb-16 ui-shadow-01',
      subtleBorder ? 'ui-border-subtle ui-bg-fill' : 'ui-border-default ui-bg-fill',
      widthClass,
      emphasized ? 'ui-bg-surface-brand-hover ui-shadow-03' : '',
      !disabled &&
        'ui-transition-all ui-duration-200 hover:ui-bg-surface-brand-hover hover:ui-shadow-01-03',
      (!disabled && isCardLink) || onClick ? 'ui-cursor-pointer' : '',
      disabled && !onClick ? 'ui-cursor-default' : '',
      className,
    );

    const catalogLinkMainClass = cn(
      'ui-flex ui-min-w-0 ui-w-full ui-flex-col ui-gap-16 ui-text-inherit ui-no-underline',
      'focus-visible:ui-outline focus-visible:ui-outline-2 focus-visible:ui-outline-offset-2 focus-visible:ui-outline-brand',
    );

    const catalogRatingBlockInner = (
      <div className="ui-flex ui-min-w-0 ui-shrink-0 ui-items-center ui-gap-4">
        <Icon name={faStar} size="xs" color={hasRatedValue ? 'yellow' : 'disabled'} />
        <TruncatedWithTooltip
          title={ratingDisplayText}
          position="top"
          maxVisibleChars={5}
          className={cn(
            'ui-text-[10px] ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-[2px]',
            emphasized ? 'ui-text-brand' : 'ui-text-tertiary',
          )}
        >
          {ratingDisplayText}
        </TruncatedWithTooltip>
      </div>
    );

    const catalogRatingBlock =
      hasCategory || hasRatedValue ? (
        ratingBlockTooltip ? (
          <Tooltip label={ratingBlockTooltip} position="top" preserveLayout>
            {catalogRatingBlockInner}
          </Tooltip>
        ) : (
          catalogRatingBlockInner
        )
      ) : null;

    const catalogMainBlocks = (
      <>
        <div className="ui-relative ui-h-[141px] ui-w-full ui-shrink-0 ui-overflow-hidden ui-rounded-t-[var(--ui-radius-surface-card)]">
          <div className="ui-absolute ui-inset-0">
            {/* Siempre bloque media; sin URL `Picture` aplica su recurso por defecto (placeholder). */}
            <Picture
              src={imageSrc ?? ''}
              alt={imageAlt}
              fillParent
              size="auto"
              radius="none"
              shadow
              border={false}
              disabled={disabled}
              className="ui-absolute ui-inset-0 ui-select-none"
            />
          </div>
          {showExclusiveBadge ? (
            <div className="ui-pointer-events-none ui-absolute ui-left-8 ui-top-8 ui-z-10">
              <Icon
                name={faBadgeCheck}
                size="sm"
                padding="xxs"
                colorDuotonePrimary="green"
                colorDuotoneSecondary="success"
                opacityDuotonePrimary={0.4}
                opacityDuotoneSecondary={1}
                background
                backgroundColor="white"
              />
            </div>
          ) : null}
          {resolvedIndicators.length > 0 ? (
            <div className="ui-pointer-events-none ui-absolute ui-right-8 ui-top-8 ui-z-10 ui-flex ui-items-center ui-gap-4">
              {resolvedIndicators.map((indicator, index) => {
                if (isCatalogCardMediaIndicatorText(indicator)) {
                  const {
                    id,
                    label,
                    tooltip,
                    tooltipProps,
                    className,
                    color = 'gray',
                    theme = 'soft',
                    size = 'lg',
                    stroke = false,
                    disabled = false,
                  } = indicator;
                  const key = id ?? `media-indicator-text-${index}`;
                  const textWrap = (
                    <span className="ui-pointer-events-auto ui-inline-flex ui-shrink-0">
                      <Badge
                        label={label}
                        color={color}
                        theme={theme}
                        size={size}
                        stroke={stroke}
                        disabled={disabled}
                        truncateLabel
                        hover={false}
                        className={cn(
                          // `Badge` md/lg usa `rounded-control-sm`; aquí forzamos pastilla como en chips de tarjeta.
                          'ui-rounded-control-pill',
                          'ui-max-w-[min(100%,140px)]',
                          color === 'gray' && '!ui-text-primary [&_span]:!ui-text-primary',
                          className,
                        )}
                      />
                    </span>
                  );
                  if (tooltip !== null && tooltip !== undefined && String(tooltip).length > 0) {
                    return (
                      <Tooltip
                        key={key}
                        label={tooltip}
                        position="top"
                        preserveLayout
                        {...tooltipProps}
                      >
                        {textWrap}
                      </Tooltip>
                    );
                  }
                  return <Fragment key={key}>{textWrap}</Fragment>;
                }
                const { id, tooltip, tooltipProps, ...iconProps } = indicator;
                const key = id ?? `media-indicator-${index}`;
                const iconWrap = (
                  <span className="ui-pointer-events-auto ui-inline-flex ui-shrink-0">
                    <Icon {...iconProps} size="xs" padding="xs" />
                  </span>
                );
                if (tooltip !== null && tooltip !== undefined && String(tooltip).length > 0) {
                  return (
                    <Tooltip
                      key={key}
                      label={tooltip}
                      position="top"
                      preserveLayout
                      {...tooltipProps}
                    >
                      {iconWrap}
                    </Tooltip>
                  );
                }
                return <Fragment key={key}>{iconWrap}</Fragment>;
              })}
            </div>
          ) : null}
        </div>

        <div className="ui-flex ui-flex-col ui-gap-4 ui-px-16">
          {showCategoryMetaRow ? (
            <div
              className={cn(
                'ui-flex ui-w-full ui-items-center ui-gap-8',
                hasCategory ? 'ui-justify-between' : 'ui-justify-end',
              )}
            >
              {categoryLabel?.trim() ? (
                <div className="ui-min-w-0 ui-flex-1 ui-overflow-hidden">
                  <TruncatedWithTooltip
                    title={categoryLabel}
                    position="top"
                    className={cn(
                      'ui-text-[10px] ui-font-body-xs-regular ui-uppercase ui-leading-body-xs-regular ui-tracking-[2px]',
                      emphasized
                        ? 'ui-text-brand'
                        : disabled
                          ? 'ui-text-tertiary'
                          : 'ui-text-tertiary group-hover:ui-text-brand',
                    )}
                  >
                    {categoryLabel}
                  </TruncatedWithTooltip>
                </div>
              ) : null}
              {catalogRatingBlock}
            </div>
          ) : null}
          <div className="ui-w-full ui-min-w-0">
            <TruncatedWithTooltip
              title={title}
              position="top"
              className={cn(
                'ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm',
                emphasized
                  ? 'ui-text-brand'
                  : disabled
                    ? 'ui-text-primary'
                    : 'ui-text-primary group-hover:ui-text-brand',
              )}
            >
              {title}
            </TruncatedWithTooltip>
          </div>
          {showLocationRow ? (
            <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-4">
              <span
                className={cn(
                  'ui-inline-flex ui-shrink-0',
                  emphasized
                    ? 'ui-text-brand'
                    : disabled
                      ? 'ui-text-secondary'
                      : 'ui-text-secondary group-hover:ui-text-brand',
                )}
              >
                <Icon name={faLocationDot} size="xs" color="current" />
              </span>
              <div className="ui-min-w-0 ui-flex-1 ui-overflow-hidden">
                <TruncatedWithTooltip
                  title={locationLabel}
                  position="top"
                  className={cn(
                    'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
                    emphasized
                      ? 'ui-text-brand'
                      : disabled
                        ? 'ui-text-secondary'
                        : 'ui-text-secondary group-hover:ui-text-brand',
                  )}
                >
                  {locationLabel}
                </TruncatedWithTooltip>
              </div>
            </div>
          ) : null}
          {resolvedTags.length > 0 ? (
            <CatalogCatalogCardTagsRow
              tags={resolvedTags}
              emphasized={emphasized}
              disabled={disabled}
            />
          ) : null}
        </div>
      </>
    );

    const catalogFooter = (
      <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-12 ui-px-16">
        {optionsTrigger}
        <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-1">{primaryCta}</div>
      </div>
    );

    return (
      <div
        ref={ref}
        role={rootCardClickActive ? 'button' : undefined}
        tabIndex={rootCardClickActive ? 0 : undefined}
        aria-disabled={disabled && !onClick ? true : undefined}
        data-selected={selected ? true : undefined}
        data-disabled={disabled ? true : undefined}
        className={rootClassName}
        onClick={rootCardClickActive ? handleRootClick : undefined}
        onKeyDown={rootCardClickActive ? handleKeyDown : undefined}
      >
        {effectiveCardLink ? (
          <a
            href={cardDestination}
            target={cardLinkTarget}
            rel={cardLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
            className={catalogLinkMainClass}
            aria-label={title}
            onClick={() => {
              onClick?.(cardContext);
            }}
          >
            {catalogMainBlocks}
          </a>
        ) : (
          catalogMainBlocks
        )}
        {catalogFooter}
      </div>
    );
  },
);

CatalogCatalogCard.displayName = 'CatalogCatalogCard';
